import { capacitorErrorMessage } from "@/lib/capacitorErrors";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Download, Loader2, MessageSquare } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { fetchConnectedFileTransfer, canWorkWithConnectedFile, queueConnectedFile, clearConnectedFile, type ConnectedFileProvider, type ConnectedFileSource } from "@/lib/connectedFiles";
import { supabase } from "@/integrations/supabase/client";
import { fileWriteUuid } from "@/lib/fileWriteProof";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import { triggerBlobDownload } from "@/lib/downloadFile";

export function ConnectedFileActions({ provider, itemId, ownerId, onOpenMirror, source, sourceName, requireSource = false }: {
  provider: ConnectedFileProvider; itemId: string; ownerId: string; onOpenMirror: () => void; source?: ConnectedFileSource; sourceName?: string; requireSource?: boolean;
}) {
  const { t } = useLanguage(), navigate = useNavigate();
  const [busy, setBusy] = useState<"download" | "mirror" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const epoch = useRef(0), lock = useRef(false), controller = useRef<AbortController | null>(null);
  const translate = useRef(t); translate.current = t;
  const sourceSid = source?.authSessionId, sourceConnection = source?.connectionId;
  const native = Capacitor.isNativePlatform();
  useEffect(() => {
    setBusy(null); setError(null);
    const invalidate = () => { epoch.current++; controller.current?.abort(); lock.current = false; };
    const subscription = sourceSid ? supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user.id !== ownerId || connectedFileReadSessionId(session.access_token) !== sourceSid) {
        invalidate(); setBusy(null); setError(translate.current("mirror.files.full.accountChanged"));
      }
    }).data.subscription : undefined;
    return () => { invalidate(); subscription?.unsubscribe(); };
  }, [provider, itemId, ownerId, sourceSid, sourceConnection]);
  const transfer = async (mode: "download" | "mirror") => {
    if (lock.current || !ownerId || mode === "download" && native || provider === "dropbox" && !source || mode === "mirror" && provider === "dropbox" && sourceName && !canWorkWithConnectedFile(sourceName)) return;
    lock.current = true;
    const ticket = epoch.current, aborter = new AbortController();
    controller.current = aborter;
    const timeout = setTimeout(() => aborter.abort(), 60_000);
    setBusy(mode); setError(null);
    try {
      if (requireSource && (!source || !fileWriteUuid(source.authSessionId) || !fileWriteUuid(source.connectionId))) throw new Error("account_changed");
      const { file, origin } = await fetchConnectedFileTransfer(provider, itemId, ownerId, aborter.signal, source);
      if (ticket !== epoch.current || aborter.signal.aborted) return;
      if (mode === "download") triggerBlobDownload(file, file.name);
      else {
        if (!canWorkWithConnectedFile(file.name)) throw new Error("unsupported_mirror_file");
        queueConnectedFile(ownerId, file, origin);
        try { onOpenMirror(); navigate("/mirror"); } catch (cause) { clearConnectedFile(); throw cause; }
      }
    } catch (cause) {
      if (ticket !== epoch.current) return;
      const code = capacitorErrorMessage(cause, "");
      setError(t(code === "file_too_large" ? "mirror.files.full.tooLarge" :
        code === "unsupported_mirror_file" ? "mirror.files.full.unsupported" :
        code === "file_not_downloadable" ? "mirror.files.full.notDownloadable" :
        code === "account_changed" || code === "connection_changed" ? "mirror.files.full.accountChanged" :
        code === "quota_exceeded" ? "mirror.files.full.quota" : "mirror.files.full.error"));
    } finally {
      clearTimeout(timeout);
      if (ticket === epoch.current) { lock.current = false; setBusy(null); }
    }
  };
  return <div data-sensitive className="ph-no-capture space-y-2">
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <button type="button" disabled={!!busy || native || provider === "dropbox" && !source} onClick={() => void transfer("download")} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border px-3 text-sm font-semibold disabled:opacity-50">
        {busy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{t("mirror.files.full.download")}
      </button>
      {(provider !== "dropbox" || !sourceName || canWorkWithConnectedFile(sourceName)) && <button type="button" disabled={!!busy || provider === "dropbox" && !source} onClick={() => void transfer("mirror")} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
        {busy === "mirror" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}{t("mirror.files.full.work")}
      </button>}
    </div>
    <p className="text-xs text-muted-foreground">{t("mirror.files.full.hint")}</p>
    {native && <p className="text-xs text-muted-foreground">{t("mirror.files.full.browserDownload")}</p>}
    {busy && <p role="status" className="text-xs text-muted-foreground">{t("mirror.files.full.loading")}</p>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
  </div>;
}
